import { ExternalAppStatus } from "../../generated/enums"
import { IHolding } from "../../holding"
import { AppSession } from "../appSession"
import { IAppDefinition, IAppOrganisation } from "../types"
import { ConnectionSyncConfig } from "./syncConfig"

/** @internal */
export class ConnectionVM {

    private _ready = false

    private _sync: ConnectionSyncConfig
    private _organisation: IAppOrganisation;

    constructor(
        public provider: IAppDefinition,
        public readonly holding: IHolding,
        public readonly session: AppSession) {


    }

    private refresh() {

        this._sync = this.provider.holdingSync
            ? new ConnectionSyncConfig(this.provider, this.holding, this.session)
            : null

    }

    get name() {
        return this.provider.name
    }

    get ready() {
        return this._ready
    }

    get img() {
        return this.provider.icon || this.provider.logo
    }

    get connectPrompt() {
        return this.provider.connectPrompt
    }

    get hasOrganisations(): boolean {
        return this.session.hasOrganisations;
    }

    get orgRequired() {
        return this.session.orgRequired
    }

    get orgLabel() {
        return this.session.orgLabel
    }

    get orgMsg() {
        return this.session.orgMsg
    }


    get sync() {
        return this._sync
    }

    get organisations(): IAppOrganisation[] {
        return this.session ? this.session.getOrganisations() : null;
    }

    get organisation() {
        return this._organisation;
    }

    set organisation(value) {
        this._organisation = value;
    }

    get organisationChanged(): boolean {
        const org = this.session.getDefaultOrganisation()
        return this.organisation !== org && (!org || !this.organisation || (this.organisation.id !== org.id))
    }

    get status() {
        return this.session?.status
    }

    get valid() {
        return this.status === ExternalAppStatus.Ok
    }

    get connected(): boolean {
        return this.session?.connected
    }

    get owerName() {
        return this.session?.ownerName
    }

    get otherOwner() {
        return this.session?.otherOwner
    }

    public setup(): ng.IPromise<boolean> {

        return this.session.getConnection(true)
            .then(r => {

                if (r) {
                    this._organisation = this.session.getDefaultOrganisation()
                    this.refresh()
                }

                return !!r

            })
            .finally(() => this._ready = true)

    }

    public refreshConfig(): ng.IPromise<any> {

        const { session } = this

        this._ready = false

        return session.refreshConfig()
            .then(o => {

                this.refresh()

                const def = session.getDefaultOrganisation()

                if (def) {
                    this._organisation = def
                }
                else if (o && o.length === 1) {
                    this._organisation = o[0]
                    return this.saveConfig()
                }

            }).finally(() => this._ready = true)


    }

    canUpdate() {
        return this.session.canUpdate()
    }

    hasChanged() {
        return !!(this.organisationChanged || (this.sync && this.sync.hasChanged()))
    }

    hasConfig() {
        return this.hasOrganisations || this.orgRequired || this.sync
    }

    canSaveConfig() {
        return this.connected && this.valid && this.hasChanged() && this.canUpdate()
    }

    saveConfig() {

        const config = this.sync ? this.sync.getConfig() : this.session.getConfig()

        return this.session.saveConfig(this._organisation, config).then(() => this.refresh())

    }

    checkSyncData() {
        return this.session.checkSyncData()
    }
}
